/**
 * useBeritaAcara.js
 * ─────────────────────────────────────────────────────────────
 * Composable yang mengenkapsulasi:
 *   - State form Berita Acara
 *   - getValidatorData()   → auto-fill dari endpoint profile
 *   - buildFormData()      → siapkan FormData untuk POST
 *   - validateForm()       → validasi frontend, kembalikan array error
 *   - submitBeritaAcara()  → POST ke endpoint + tampilkan Swal
 *
 * Dipakai di dalam ModalBeritaAcara.vue.
 * ─────────────────────────────────────────────────────────────
 */

import { reactive, ref } from "vue";
import axios from "axios";
import Swal from "sweetalert2";

// ─── Nilai default form ─────────────────────────────────────────────
function defaultForm() {
  return {
    nama: "",
    nip: "",
    alamat: "",
    unit_kerja: "",
    jabatan: "",
    nomor_telepon: "",
    nama_bank: "",
    nomor_rekening: "",
    atas_nama_rekening: "",
    nik: "",
    npwp: "",
    tanda_tangan: null, // base64 string dari SignaturePad
    kritik_saran: "", // opsional, tidak required
    nda: null, // file upload, opsional (pdf/jpg/jpeg/png, maks 5MB)
    nomor_surat: "", // opsional
    file_ktp: null, // file upload KTP baru (dikirim ke BE hanya jika user upload ulang)
    file_npwp: null, // file upload NPWP baru (dikirim ke BE hanya jika user upload ulang)
    path_ktp: null, // path KTP yang sudah tersimpan di BE (untuk preview)
    path_npwp: null, // path NPWP yang sudah tersimpan di BE (untuk preview)
  };
}

// ── Bangun URL lengkap file yang tersimpan di storage BE ────────────
function fileUrl(path) {
  if (!path) return null;
  return `${process.env.VUE_APP_BACKEND_URL}/storage/${path}`;
}

export function useBeritaAcara() {
  const form = reactive(defaultForm());
  const loadingValidator = ref(false);

  // ── Reset form ke kondisi awal ──────────────────────────────────
  function resetForm() {
    Object.assign(form, defaultForm());
  }

  // ── Auto-fill dari endpoint profile validator ────────────────────
  async function getValidatorData() {
    loadingValidator.value = true;
    try {
      const response = await axios.get(
        process.env.VUE_APP_BACKEND_URL_VERSION +
          "verifikasi/validator/profile",
        {
          headers: {
            Accept: "application/json",
            Authorization: "Bearer " + localStorage.access_token,
          },
        },
      );

      const data = response.data?.data;
      if (!data) return;

      // Mapping field — sesuaikan key jika response API berbeda
      const fieldMap = {
        nama: "nama",
        nip: "nip",
        alamat: "alamat",
        unit_kerja: "unit_kerja",
        jabatan: "jabatan",
        nomor_telepon: "nomor_telepon",
        nama_bank: "nama_bank",
        nomor_rekening: "nomor_rekening",
        atas_nama_rekening: "atas_nama_rekening",
        nik: "nik",
        npwp: "npwp",
        tanda_tangan: "tanda_tangan_raw",
      };

      Object.entries(fieldMap).forEach(([formKey, apiKey]) => {
        form[formKey] = data[apiKey] ?? "";
      });

      // File yang sudah pernah diupload sebelumnya (jika ada) — dipakai
      // untuk menampilkan tombol preview & menentukan wajib/tidaknya upload.
      form.path_ktp = data.path_ktp || null;
      form.path_npwp = data.path_npwp || null;
    } catch {
      Swal.fire({
        icon: "warning",
        title: "Perhatian",
        text: "Gagal memuat data validator. Silakan isi manual.",
      });
    } finally {
      loadingValidator.value = false;
    }
  }

  // ── Validasi frontend ────────────────────────────────────────────
  // Sesuai BeritaAcaraRequest backend: semua field wajib diisi KECUALI
  // kritik_saran dan nomor_surat (nullable di backend). NDA nullable di
  // backend, tapi diwajibkan di FE sesuai kebutuhan bisnis.
  function validateForm(isSignatureBlank) {
    const errors = [];

    // ── Data Utama ──────────────────────────────────────────────────
    if (!form.nama?.trim()) errors.push("Nama wajib diisi.");
    if (!form.nip?.trim())
      errors.push("Nomor Induk Pegawai wajib diisi.");
    if (!form.alamat?.trim())
      errors.push("Alamat Lengkap wajib diisi.");
    if (!form.unit_kerja?.trim()) errors.push("Unit Kerja wajib diisi.");
    if (!form.jabatan?.trim()) errors.push("Jabatan wajib diisi.");
    if (!form.nomor_telepon?.trim()) errors.push("Nomor Telepon wajib diisi.");
    if (!form.nama_bank?.trim()) errors.push("Nama Bank wajib diisi.");
    if (!form.nomor_rekening?.trim())
      errors.push("Nomor Rekening wajib diisi.");
    if (!form.atas_nama_rekening?.trim())
      errors.push("Atas Nama Rekening wajib diisi.");
    if (!form.nik?.trim()) errors.push("NIK wajib diisi.");
    if (!form.npwp?.trim()) errors.push("NPWP wajib diisi.");

    // ── Upload Dokumen ──────────────────────────────────────────────
    // KTP & NPWP hanya wajib diisi jika belum pernah ada file tersimpan
    // di BE (path_ktp/path_npwp). Jika sudah ada, user boleh tidak
    // upload ulang — file lama akan tetap dipakai.
    if (!form.file_ktp && !form.path_ktp) errors.push("Upload KTP wajib diisi.");
    if (!form.file_npwp && !form.path_npwp)
      errors.push("Upload NPWP wajib diisi.");
    if (!form.nda) errors.push("Upload NDA wajib diisi.");

    // ── Tanda Tangan ────────────────────────────────────────────────
    if (isSignatureBlank) errors.push("Tanda tangan wajib diisi.");

    // kritik_saran, nda, nomor_surat → tidak divalidasi (opsional)

    return errors;
  }

  // ── Bangun FormData untuk multipart POST ─────────────────────────
  function buildFormData() {
    const fd = new FormData();

    const textFields = [
      "nama",
      "nip",
      "alamat",
      "unit_kerja",
      "jabatan",
      "nomor_telepon",
      "nama_bank",
      "nomor_rekening",
      "atas_nama_rekening",
      "nik",
      "npwp",
      "tanda_tangan",
      "kritik_saran",
      "nomor_surat"
    ];

    textFields.forEach((key) => {
      if (form[key] !== null && form[key] !== undefined) {
        fd.append(key, form[key]);
      }
    });

    if (form.nda) fd.append("nda", form.nda);
    if (form.file_ktp) fd.append("file_ktp", form.file_ktp);
    if (form.file_npwp) fd.append("file_npwp", form.file_npwp);

    return fd;
  }

  // ── Submit ke API ────────────────────────────────────────────────
  async function submitBeritaAcara(kompetensiLevelId, { onSuccess } = {}) {
    Swal.fire({
      title: "Sedang memproses data...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await axios({
        method: "post",
        url:
          process.env.VUE_APP_BACKEND_URL_VERSION +
          "verifikasi/kompetensi-level/" +
          kompetensiLevelId +
          "/berita-acara",
        data: buildFormData(),
        headers: {
          Accept: "application/json",
          Authorization: "Bearer " + localStorage.access_token,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.meta?.code === 200) {
        await Swal.fire({
          icon: "success",
          title: "Berhasil",
          text: response.data.meta.message,
          timer: 2000,
          showConfirmButton: false,
        });

        if (typeof onSuccess === "function") onSuccess();
      } else {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: response.data?.meta?.message ?? "Terjadi kesalahan.",
        });
      }
    } catch (error) {
      const metaMessage = error?.response?.data?.meta?.message;
      const validationErrors = error?.response?.data?.data;
      let message = "Terjadi kesalahan.";

      if (
        metaMessage === "Validation Failed" &&
        Array.isArray(validationErrors)
      ) {
        message = `<ul style="text-align:left;">${validationErrors
          .map((e) => `<li>${e}</li>`)
          .join("")}</ul>`;
      } else if (metaMessage) {
        message = metaMessage;
      }

      Swal.fire({ icon: "error", title: "Oops...", html: message });
    }
  }

  return {
    form,
    loadingValidator,
    resetForm,
    getValidatorData,
    validateForm,
    submitBeritaAcara,
    fileUrl,
  };
}
